/*
 * Notification
 * @module components
 * @flow
 */

import React from 'react';

import styled, { css } from 'styled-components/macro';
import { ifNotProp } from 'styled-tools';
import { font, key } from 'styled-theme';

// $FlowIssue
import { ReactComponent as CloseIcon } from '@/icons/ic-close.svg';
// $FlowIssue
import { ReactComponent as SuccessIcon } from '@/icons/ic-success.svg';
// $FlowIssue
import { ReactComponent as FailIcon } from '@/icons/fail.svg';
// $FlowIssue
import { ReactComponent as InfoIcon } from '@/icons/info.svg';

export type NotificationProps = $Shape<NotificationsListItem> & {
  onClose?: () => void,
};

const mainColor = ({ type }: NotificationProps) =>
  key(`notification.${type}.0`);
const accentColor = ({ type }: NotificationProps) =>
  key(`notification.${type}.1`);

const NotificationWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s;
  padding: 18px 25px 18px 20px;
  ${ifNotProp(
    'fullWidth',
    css`
      min-width: 300px;
      max-width: 400px;
    `,
  )}

  border-radius: 6px;
  background: #fff;
  border: solid 1px ${mainColor};
  color: ${mainColor};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-image: ${accentColor};
  }
`;

const IconWrapper = styled.div`
  display: inline-block;
  margin-right: 18px;
`;

const NotificationClose = styled(CloseIcon)`
  position: absolute;
  top: 0;
  padding: 8px;
  right: 0;
  width: 12px;
  height: 12px;
  cursor: pointer;
`;

const NotificationMessage = styled.div`
  font-family: ${font('primary')};
  font-size: 16px;
`;

/**
 * Notification React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {number} [props.showDelay=4e3] - showing delay
 * @param {message} [props.message] - transparent button with border
 * @param {boolean} [props.onClose] - event for close notification
 * @param {'success' | 'fail'| 'info'} [props.type='success'] - color schema
 * @example <caption>Simple usage</caption>
 * <Notification message="Hello world" />
 * @returns {React.Node<*>} React-component
 */
export const Notification = ({
  message,
  rawMessage,
  onClose,
  type,
  ...restProps
}: NotificationProps) => (
  <NotificationWrapper type={type} {...restProps} testid="notification">
    <NotificationClose type={type} onClick={onClose} />
    {type && (
      <IconWrapper>
        {type === 'success' ? <SuccessIcon /> : ''}
        {type === 'fail' ? <FailIcon /> : ''}
        {type === 'info' ? <InfoIcon /> : ''}
      </IconWrapper>
    )}
    <NotificationMessage type={type}>
      <span>{message}</span>
      {type === 'fail' && <span> : {rawMessage}</span>}
    </NotificationMessage>
  </NotificationWrapper>
);

Notification.defaultProps = {
  delayInterval: 4e3,
  type: 'success',
  onClose: () => {},
};
